import { relataHoras, type Mes } from '@sheafyield/dominio';
import { useRef, useState } from 'react';
import { Botao } from '../ds/Botao.js';
import { Campo } from '../ds/Campo.js';
import { CampoLongo } from '../ds/CampoLongo.js';
import { Folha } from '../ds/Folha.js';
import { Selecao } from '../ds/Selecao.js';
import { formatarDuracao } from '../hoje/relogio.js';
import {
  RelatorioAlterado,
  type EdicaoDoMes,
  type VisaoDoRelatorio,
} from './dados.js';

interface Props {
  visao: VisaoDoRelatorio;
  identidade: string;
  aoSalvar: (entrada: EdicaoDoMes, revisao: string) => Promise<void>;
  aoRetomar: (revisao: string) => Promise<void>;
  aoAtualizar: () => Promise<VisaoDoRelatorio>;
  aoFechar: () => void;
}
const rascunhoDe = (mes: Mes) => ({
  participou: mes.participou,
  estudos: String(mes.estudos),
  horas: String(mes.horas_minutos / 60),
  creditos: String(mes.creditos_minutos),
  observacao: mes.observacao,
});
function inteiro(valor: string, nome: string, teto = Number.MAX_SAFE_INTEGER) {
  if (
    !/^\d+$/.test(valor) ||
    !Number.isSafeInteger(Number(valor)) ||
    Number(valor) > teto
  )
    throw new Error(
      `${nome}: informe um número inteiro entre 0 e ${teto === Number.MAX_SAFE_INTEGER ? 'o limite permitido' : String(teto)}.`,
    );
  return Number(valor);
}
function Comparacao({
  titulo,
  mes,
  comHoras,
}: {
  titulo: string;
  mes: Mes;
  comHoras: boolean;
}) {
  return (
    <section aria-label={titulo}>
      <h3>{titulo}</h3>
      <dl className="relatorio__comparacao">
        <dt>Participação</dt>
        <dd>{mes.participou ? 'Participei' : 'Não participei'}</dd>
        <dt>Estudos</dt>
        <dd>{mes.estudos}</dd>
        <dt>Observação</dt>
        <dd>{mes.observacao || 'Sem observação'}</dd>
        {comHoras ? (
          <>
            <dt>Horas</dt>
            <dd>{formatarDuracao(mes.horas_minutos)}</dd>
            <dt>Créditos</dt>
            <dd>{formatarDuracao(mes.creditos_minutos)}</dd>
            <dt>Transporte</dt>
            <dd>{formatarDuracao(mes.transporte_minutos)}</dd>
          </>
        ) : null}
      </dl>
    </section>
  );
}
export function FolhaEditarMes({
  visao,
  identidade,
  aoSalvar,
  aoRetomar,
  aoAtualizar,
  aoFechar,
}: Props) {
  const [rascunho, setRascunho] = useState(() => rascunhoDe(visao.atual));
  const [origem, setOrigem] = useState({ identidade, revisao: visao.revisao });
  const [atual, setAtual] = useState(visao);
  const [revisaoDoRascunho, setRevisaoDoRascunho] = useState(visao.revisao);
  const [previa, setPrevia] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [conflitoManual, setConflitoManual] = useState(false);
  const [operacao, setOperacao] = useState<'salvar' | 'retomar' | null>(null);
  const emCurso = useRef(false);
  const formulario = useRef<HTMLFormElement>(null);
  // O rascunho pertence a um dono/período. Uma releitura da mesma identidade
  // atualiza a comparação, mas nunca substitui o que a pessoa está digitando.
  if (origem.identidade !== identidade) {
    setOrigem({ identidade, revisao: visao.revisao });
    setAtual(visao);
    setRascunho(rascunhoDe(visao.atual));
    setRevisaoDoRascunho(visao.revisao);
    setPrevia(null);
    setErro(null);
    setConflitoManual(false);
  } else if (origem.revisao !== visao.revisao) {
    setOrigem({ identidade, revisao: visao.revisao });
    setAtual(visao);
  }
  const comHoras = relataHoras(atual.perfil.tipo);
  const mudouNaPrevia = previa !== null && previa !== atual.revisao;
  async function executar(tipo: 'salvar' | 'retomar') {
    if (emCurso.current) return;
    if (tipo === 'retomar' && (!atual.baseCompleta || mudouNaPrevia)) {
      if (mudouNaPrevia) setPrevia(atual.revisao);
      return;
    }
    emCurso.current = true;
    setOperacao(tipo);
    setOcupado(true);
    setErro(null);
    try {
      if (tipo === 'salvar') {
        const entrada: EdicaoDoMes = {
          participou: rascunho.participou,
          estudos: inteiro(rascunho.estudos, 'Estudos'),
          horas_minutos: comHoras
            ? inteiro(
                rascunho.horas,
                'Horas',
                Math.floor(Number.MAX_SAFE_INTEGER / 60),
              ) * 60
            : atual.atual.horas_minutos,
          creditos_minutos: comHoras
            ? inteiro(rascunho.creditos, 'Créditos', 3300)
            : atual.atual.creditos_minutos,
          observacao: rascunho.observacao,
        };
        await aoSalvar(entrada, revisaoDoRascunho);
      } else await aoRetomar(atual.revisao);
      aoFechar();
    } catch (causa) {
      if (causa instanceof RelatorioAlterado) {
        try {
          const nova = await aoAtualizar();
          setAtual(nova);
          setRevisaoDoRascunho(nova.revisao);
          setErro(
            'Os dados deste mês mudaram. Confira os valores atualizados antes de salvar.',
          );
          // Uma comparação nova exige revisão e outro toque de confirmação.
          if (tipo === 'retomar') setPrevia('revisar');
          else {
            setConflitoManual(true);
            setPrevia(null);
          }
        } catch (falha) {
          setErro(
            falha instanceof Error
              ? falha.message
              : 'Não deu para atualizar os valores. Tente novamente.',
          );
        }
      } else
        setErro(
          causa instanceof Error
            ? causa.message
            : 'Não deu para salvar. Tente novamente.',
        );
    } finally {
      emCurso.current = false;
      setOcupado(false);
      setOperacao(null);
    }
  }
  return (
    <Folha
      aberta
      titulo="Editar totais"
      focoInicial={formulario}
      aoFechar={() => (emCurso.current ? false : aoFechar())}
    >
      <form
        ref={formulario}
        tabIndex={-1}
        className="relatorio__formulario"
        noValidate
        onSubmit={(evento) => {
          evento.preventDefault();
          void executar('salvar');
        }}
      >
        <fieldset disabled={ocupado}>
          <p>
            Estes ajustes substituem os totais do mês. Os registros de atividade
            continuam guardados.
          </p>
          <p>Participação</p>
          <Selecao
            rotulo="Participação"
            valor={rascunho.participou ? 'sim' : 'nao'}
            opcoes={[
              { valor: 'sim', rotulo: 'Participei' },
              { valor: 'nao', rotulo: 'Não participei' },
            ]}
            aoMudar={(valor) =>
              setRascunho({ ...rascunho, participou: valor === 'sim' })
            }
          />
          <Campo
            rotulo="Estudos"
            type="number"
            min={0}
            step={1}
            value={rascunho.estudos}
            onChange={(e) =>
              setRascunho({ ...rascunho, estudos: e.target.value })
            }
          />
          {comHoras ? (
            <>
              <Campo
                rotulo="Horas"
                type="number"
                min={0}
                step={1}
                value={rascunho.horas}
                onChange={(e) =>
                  setRascunho({ ...rascunho, horas: e.target.value })
                }
              />
              <Campo
                rotulo="Créditos (minutos)"
                type="number"
                min={0}
                max={3300}
                step={1}
                value={rascunho.creditos}
                onChange={(e) =>
                  setRascunho({ ...rascunho, creditos: e.target.value })
                }
              />
            </>
          ) : null}
          <CampoLongo
            rotulo="Observação"
            value={rascunho.observacao}
            onChange={(e) =>
              setRascunho({ ...rascunho, observacao: e.target.value })
            }
          />
        </fieldset>
        {conflitoManual ? (
          <>
            <Comparacao
              titulo="Valores atuais"
              mes={atual.atual}
              comHoras={comHoras}
            />
            <p>
              Seu rascunho foi mantido nos campos acima. Compare com os valores
              atuais antes de salvar novamente.
            </p>
          </>
        ) : null}
        {/* O botão fica fora do fieldset desabilitado: carregando preserva
            foco e anuncia progresso sem permitir outra gravação. */}
        <Botao
          type="submit"
          largura="cheia"
          carregando={operacao === 'salvar'}
          disabled={operacao === 'retomar'}
        >
          Salvar ajustes
        </Botao>
        {erro ? <p role="alert">{erro}</p> : null}
        {!atual.baseCompleta ? (
          <p>
            Aguarde a sincronização completa para usar o cálculo automático.
          </p>
        ) : null}
        {previa === null ? (
          <Botao
            variante="secundario"
            disabled={ocupado || !atual.baseCompleta}
            onClick={() => {
              setConflitoManual(false);
              setPrevia(atual.revisao);
            }}
          >
            Usar cálculo automático
          </Botao>
        ) : (
          <>
            <Comparacao
              titulo="Valores atuais"
              mes={atual.atual}
              comHoras={comHoras}
            />
            <Comparacao
              titulo="Cálculo automático"
              mes={atual.automatico}
              comHoras={comHoras}
            />
            <p>
              Observação e marcação de envio serão preservadas. O relatório pode
              diferir do texto compartilhado anteriormente.
            </p>
            {mudouNaPrevia ? (
              <p role="status">
                Os valores mudaram. Revise a nova comparação antes de confirmar.
              </p>
            ) : null}
            <Botao
              disabled={operacao === 'salvar' || !atual.baseCompleta}
              carregando={operacao === 'retomar'}
              onClick={() => {
                void executar('retomar');
              }}
            >
              {mudouNaPrevia
                ? 'Revisar valores atualizados'
                : 'Confirmar cálculo automático'}
            </Botao>
            <Botao
              variante="texto"
              disabled={ocupado}
              onClick={() => setPrevia(null)}
            >
              Cancelar
            </Botao>
          </>
        )}
      </form>
    </Folha>
  );
}
